import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CurationStore } from '../types/curation';
import { SupportedCurrency, getCurrencyFromLanguage } from '../utils/currencyUtils';
import { 
  getCurationStoreLocalizedName,
  formatMinOrderAmount,
  formatRating,
  formatReviewCount,
  formatDistance
} from '../utils/formatUtils';
import { getCurrentLanguage } from '../config/languageConfig';
import ImageWithFallback from './ImageWithFallback';
import useTranslation from '../hooks/useTranslation';
import { trackCurationStoreClick } from '../services/AnalyticsService';

interface CurationRestaurantItemProps {
  store: CurationStore;
  currency?: SupportedCurrency;
  curationName?: string; // 큐레이션 테마 이름 추가
}

const CurationRestaurantItem: React.FC<CurationRestaurantItemProps> = ({
  store,
  currency,
  curationName
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  
  // 현재 언어에 맞는 통화 설정
  const effectiveCurrency = useMemo(() => {
    return getCurrencyFromLanguage(currentLang);
  }, [currentLang]);

  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener('language-changed', handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, []);

  // 다국어 이름 가져오기
  const displayName = getCurationStoreLocalizedName(store);

  // 최소주문금액 포맷팅
  const minOrderText = formatMinOrderAmount(store, effectiveCurrency);

  // 평점 정보 포맷팅 (0인 경우 표시하지 않음)
  const hasRating = store.reviewAvg != null && store.reviewAvg > 0;
  const ratingText = hasRating ? formatRating(store.reviewAvg!) : "";
  const reviewCountText = hasRating && store.reviewCount != null && store.reviewCount > 0
    ? formatReviewCount(store.reviewCount!)
    : "";

  // 레스토랑 상세 페이지로 이동
  const handleClick = () => {
    // GA 이벤트 전송
    if (curationName) {
      trackCurationStoreClick(curationName);
    }
    
    navigate(`/restaurant/${store.id}`);
  };

  return (
    <div
      className="flex-shrink-0 w-72 bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105"
      onClick={handleClick}
    >
      {/* 레스토랑 배경 이미지 */}
      <div className="h-32 overflow-hidden relative">
        <ImageWithFallback
          src={store.backgroundUrl || store.logoUrl}
          alt={displayName}
          className="w-full h-full object-cover"
          fallback="https://delivereee.github.io/home/images/restaurants/default-restaurant.jpg"
        />

        {/* 오픈 여부 배지 (필요시) */}
        {!store.isOpen && (
          <div className="absolute top-3 right-3 bg-gray-800 bg-opacity-75 text-white text-xs px-2 py-1 rounded-full">
            Closed
          </div>
        )}
      </div>

      {/* 레스토랑 정보 */}
      <div className="p-3">
        {/* 가게 이름 */}
        <h3 className="text-base font-bold mb-2 text-left text-gray-900 line-clamp-1">
          {displayName}
        </h3>

        {/* 평점, 리뷰 갯수, 거리 및 최소 주문 금액을 좌측 정렬로 표시 */}
        <div className="flex items-center text-xs text-gray-500 space-x-2">
          {/* 평점 및 리뷰 갯수 */}
          {hasRating && (
            <div className="flex items-center">
              {/* 별 아이콘 */}
              <svg
                className="w-3 h-3 text-yellow-400 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-medium text-gray-700">
                {ratingText}
              </span>
              {reviewCountText && (
                <span className="text-gray-500 ml-1">({reviewCountText})</span>
              )}
            </div>
          )}
          
          {/* 구분점 (평점이 있는 경우에만) */}
          {hasRating && (
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
          )}
          
          {/* 거리 정보 */}
          {store.distance > 0 && (
            <div className="font-medium">
              {formatDistance(store.distance, t)}
            </div>
          )}
          
          {/* 구분점 */}
          {store.distance > 0 && (
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
          )}
          
          {/* 최소 주문 금액 */}
          <div className="font-medium">
            <span className="text-gray-500">
              {t("curation.minOrder")}{" "}
            </span>
            <span className="text-gray-700">{minOrderText}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurationRestaurantItem;
